import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { fetchBootstrap } from '../api'
import { fallbackData } from '../data/fallback'
import type { SiteBootstrap } from '../types'

interface CmsState {
  data: SiteBootstrap
  loading: boolean
  offlineFallback: boolean
  refresh: () => Promise<void>
}

const CmsContext = createContext<CmsState>({ data: fallbackData, loading: true, offlineFallback: false, refresh: async () => undefined })

export function CmsProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<SiteBootstrap>(fallbackData)
  const [loading, setLoading] = useState(true)
  const [offlineFallback, setOfflineFallback] = useState(false)

  const refresh = async () => {
    setLoading(true)
    try {
      const response = await fetchBootstrap()
      setData(response)
      setOfflineFallback(false)
    } catch (error) {
      console.warn('WebsMetrix API unavailable; using bundled launch content.', error)
      setData(fallbackData)
      setOfflineFallback(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void refresh() }, [])
  const value = useMemo(() => ({ data, loading, offlineFallback, refresh }), [data, loading, offlineFallback])
  return <CmsContext.Provider value={value}>{children}</CmsContext.Provider>
}

export const useCms = () => useContext(CmsContext)
